import React, { useEffect, useState } from 'react';
import { CheckCircle2, ShieldCheck, Star, X, ChevronDown, ChevronUp, Lock, Smartphone, BookOpen, FileText, AlertTriangle, ArrowRight, Clock, Search, ListChecks, HelpCircle } from 'lucide-react';
import { OrderBumps } from './components/OrderBumps';
import { RecentPurchaseToast } from './components/RecentPurchaseToast';
import { ExitIntentPopup } from './components/ExitIntentPopup';
const Modal = ({
  isOpen,
  onClose,
  title,
  children
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) => {
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" onClick={onClose} data-id="element-0">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-1">
        <div className="flex items-center justify-between px-8 py-5 border-b border-gray-200" data-id="element-2">
          <h2 className="font-serif text-2xl font-bold text-brand-dark" data-id="element-3">
            {title}
          </h2>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100 transition-colors" aria-label="Close" data-id="element-4">
            <X className="w-6 h-6 text-gray-500" data-id="element-5" />
          </button>
        </div>
        <div className="px-8 py-6 overflow-y-auto max-h-[70vh] text-gray-700 text-base leading-relaxed space-y-4" data-id="element-6">
          {children}
        </div>
      </div>
    </div>;
};
const CTAButton = ({
  href,
  children,
  onClick,
  className = ''
}: {
  href: string;
  children: React.ReactNode;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  className?: string;
}) => {
  return <a href={href} onClick={onClick} className={`inline-flex items-center justify-center w-full sm:w-auto px-8 py-5 text-lg md:text-xl font-bold text-brand-dark bg-brand-gold hover:bg-brand-gold-hover transition-colors rounded-xl shadow-lg ${className}`} data-id="element-7">
      {children}
    </a>;
};
const FAQItem = ({
  question,
  answer
}: {
  question: string;
  answer: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border-b border-gray-200 last:border-0 bg-white rounded-xl mb-4 overflow-hidden shadow-sm" data-id="element-8">
      <button onClick={() => setIsOpen(!isOpen)} className="flex justify-between items-center w-full p-6 text-left focus:outline-none hover:bg-gray-50 transition-colors" aria-expanded={isOpen} data-id="element-9">
        <span className="font-serif text-lg md:text-xl font-bold text-brand-dark pr-4" data-id="element-10">
          {question}
        </span>
        {isOpen ? <ChevronUp className="w-6 h-6 text-brand-gold flex-shrink-0" data-id="element-11" /> : <ChevronDown className="w-6 h-6 text-brand-gold flex-shrink-0" data-id="element-12" />}
      </button>
      {isOpen && <div className="px-6 pb-6 text-base md:text-lg leading-relaxed text-gray-700" data-id="element-13">
          {answer}
        </div>}
    </div>;
};
export function App() {
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [showExitPopup, setShowExitPopup] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [bump1Checked, setBump1Checked] = useState(false);
  const [bump2Checked, setBump2Checked] = useState(false);
  const bumpCount = (bump1Checked ? 1 : 0) + (bump2Checked ? 1 : 0);
  const basePrice = 17;
  const totalPrice = basePrice + bumpCount * 9;
  const checkoutUrl = bump1Checked && bump2Checked ? 'https://shopdilore.com/cart/53908295614774:1' : bump1Checked ? 'https://shopdilore.com/cart/53908271464758:1' : bump2Checked ? 'https://shopdilore.com/cart/53908291289398:1' : 'https://www.shopdilore.com/cart/53835577721142:1';
  const handleCheckoutClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (!bump1Checked && !bump2Checked) {
      e.preventDefault();
      setShowExitPopup(true);
    }
  };
  useEffect(() => {
    document.title = 'The Senior Benefit Unlocker Guide';
  }, []);
  return <div className="min-h-screen bg-brand-cream font-sans text-gray-800 selection:bg-brand-gold selection:text-brand-dark pb-24 md:pb-0" data-id="element-14">
      {/* ===== 1. HERO SECTION ===== */}
      <section className="bg-brand-dark text-white pt-12 pb-16 md:pt-20 md:pb-24 px-6" data-id="element-15">
        <div className="max-w-6xl mx-auto" data-id="element-16">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16" data-id="element-17">
            <div className="flex-1 text-center lg:text-left" data-id="element-18">
              <p className="text-brand-gold font-bold uppercase tracking-widest text-sm md:text-base mb-4" data-id="element-19">
                A Plain-English Guide for Seniors, Retirees & Families
              </p>
              <h1 className="font-serif text-3xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6" data-id="element-20">
                Find Senior Benefits, Discounts, and Assistance Programs You May
                Qualify For — Without Spending Hours Digging Through Confusing
                Websites
              </h1>
              <p className="text-lg md:text-xl leading-relaxed text-gray-300 mb-8" data-id="element-21">
                The Senior Benefit Unlocker Guide helps you quickly identify
                overlooked programs, savings opportunities, and next-step
                resources in one simple digital guide you can read on any
                device.
              </p>

              <ul className="space-y-4 mb-10 text-base md:text-lg text-gray-300 text-left max-w-xl mx-auto lg:mx-0" data-id="element-22">
                <li className="flex items-start gap-3" data-id="element-23">
                  <CheckCircle2 className="w-6 h-6 text-brand-gold flex-shrink-0 mt-0.5" data-id="element-24" />
                  <span data-id="element-25">
                    See which types of senior programs and savings opportunities
                    may be worth checking
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-26">
                  <CheckCircle2 className="w-6 h-6 text-brand-gold flex-shrink-0 mt-0.5" data-id="element-27" />
                  <span data-id="element-28">
                    Understand common eligibility factors in simpler language
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-29">
                  <CheckCircle2 className="w-6 h-6 text-brand-gold flex-shrink-0 mt-0.5" data-id="element-30" />
                  <span data-id="element-31">
                    Avoid wasting time searching dozens of scattered websites
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-32">
                  <CheckCircle2 className="w-6 h-6 text-brand-gold flex-shrink-0 mt-0.5" data-id="element-33" />
                  <span data-id="element-34">
                    Includes a free application checklist bonus to help you
                    avoid common mistakes
                  </span>
                </li>
              </ul>

              <CTAButton href="#checkout" data-id="element-35">Get Instant Access Now</CTAButton>
              <div className="flex items-center justify-center lg:justify-start gap-2 mt-4 text-sm text-gray-400" data-id="element-36">
                <Lock className="w-4 h-4" data-id="element-37" />
                <span data-id="element-38">
                  Instant digital delivery • One-time payment • 30-day
                  money-back guarantee
                </span>
              </div>
            </div>

            <div className="flex-1 w-full max-w-md lg:max-w-none relative" data-id="element-39">
              <div className="absolute -top-4 -right-4 bg-brand-gold text-brand-dark font-bold text-xs uppercase tracking-wider px-4 py-2 rounded-full shadow-lg z-10 transform rotate-3" data-id="element-40">
                Instant Digital Access
              </div>
              <img src="https://cdn.magicpatterns.com/uploads/9dHnNj5kJDczNG2eEUYH8Y/The_Senior_Benefit_Unlocker_main_image.png" alt="The Senior Benefit Unlocker Guide on a tablet" className="w-full h-auto rounded-2xl shadow-2xl border border-white/10" data-id="element-41" />
            </div>
          </div>
        </div>
      </section>

      {/* ===== 2. TRUST BAR ===== */}
      <div className="bg-brand-gold text-brand-dark py-4 px-6 border-b border-brand-gold-hover" data-id="element-42">
        <div className="max-w-6xl mx-auto flex flex-wrap justify-center gap-x-8 gap-y-4 text-sm md:text-base font-bold" data-id="element-43">
          <div className="flex items-center gap-2" data-id="element-44">
            <FileText className="w-5 h-5" data-id="element-45" /> Plain-English Format
          </div>
          <div className="flex items-center gap-2" data-id="element-46">
            <Clock className="w-5 h-5" data-id="element-47" /> Instant Access
          </div>
          <div className="flex items-center gap-2" data-id="element-48">
            <Smartphone className="w-5 h-5" data-id="element-49" /> Read on Any Device
          </div>
          <div className="flex items-center gap-2" data-id="element-50">
            <ShieldCheck className="w-5 h-5" data-id="element-51" /> 30-Day Guarantee
          </div>
        </div>
      </div>

      {/* ===== 3. OPENING VSL-STYLE HOOK ===== */}
      <section className="bg-brand-cream py-16 md:py-24 px-6" data-id="element-52">
        <div className="max-w-3xl mx-auto text-center" data-id="element-53">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-brand-dark mb-8 leading-tight" data-id="element-54">
            Why so many seniors miss benefits and savings opportunities they may
            qualify for
          </h2>
          <div className="text-lg md:text-xl text-gray-700 leading-relaxed space-y-6 text-left" data-id="element-55">
            <p data-id="element-56">
              Trying to figure out what programs exist, what they cover, and
              whether it's even worth applying can feel overwhelming fast.
            </p>
            <p data-id="element-57">
              One website says one thing. Another uses confusing terms. Another
              sends you to a different office, form, or agency.
            </p>
            <p data-id="element-58">
              That confusion is exactly why many people never get far enough to
              discover what may actually be available to them.
            </p>
            <p className="font-medium text-brand-dark p-6 bg-white border-l-4 border-brand-gold rounded-r-xl shadow-sm" data-id="element-59">
              That's why we created{' '}
              <strong data-id="element-60">The Senior Benefit Unlocker Guide</strong> — to organize
              benefit categories, savings opportunities, and useful next-step
              information into one simpler resource.
            </p>
          </div>
          <div className="mt-10" data-id="element-61">
            <button onClick={() => setPreviewOpen(true)} className="inline-flex items-center justify-center w-full sm:w-auto px-8 py-5 text-lg md:text-xl font-bold text-brand-dark bg-brand-gold hover:bg-brand-gold-hover transition-colors rounded-xl shadow-lg" data-id="element-62">
              See What's Included
            </button>
          </div>
        </div>
      </section>

      {/* ===== 4. WHAT THIS IS / WHAT THIS ISN'T ===== */}
      <section className="bg-white py-16 md:py-24 px-6" data-id="element-63">
        <div className="max-w-5xl mx-auto" data-id="element-64">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-brand-dark mb-12 text-center" data-id="element-65">
            What This Guide Is — And What It Isn't
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-66">
            <div className="bg-brand-cream p-8 rounded-2xl border border-gray-200" data-id="element-67">
              <h3 className="font-serif text-2xl font-bold text-brand-dark mb-6 flex items-center gap-3" data-id="element-68">
                <CheckCircle2 className="w-7 h-7 text-green-600" data-id="element-69" /> What This Is
              </h3>
              <ul className="space-y-4 text-gray-700 text-lg" data-id="element-70">
                <li className="flex items-start gap-3" data-id="element-71">
                  <span className="w-2 h-2 rounded-full bg-brand-gold mt-2.5 flex-shrink-0" data-id="element-72"></span>
                  <span data-id="element-73">
                    A digital guide designed to help seniors and families
                    identify benefit categories, discounts, and assistance
                    programs worth exploring
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-74">
                  <span className="w-2 h-2 rounded-full bg-brand-gold mt-2.5 flex-shrink-0" data-id="element-75"></span>
                  <span data-id="element-76">
                    A plain-English resource that simplifies confusing
                    information
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-77">
                  <span className="w-2 h-2 rounded-full bg-brand-gold mt-2.5 flex-shrink-0" data-id="element-78"></span>
                  <span data-id="element-79">A shortcut to help you review options faster</span>
                </li>
                <li className="flex items-start gap-3" data-id="element-80">
                  <span className="w-2 h-2 rounded-full bg-brand-gold mt-2.5 flex-shrink-0" data-id="element-81"></span>
                  <span data-id="element-82">
                    A practical starting point for understanding what may apply
                    to your situation
                  </span>
                </li>
              </ul>
            </div>

            <div className="bg-gray-50 p-8 rounded-2xl border border-gray-200" data-id="element-83">
              <h3 className="font-serif text-2xl font-bold text-brand-dark mb-6 flex items-center gap-3" data-id="element-84">
                <X className="w-7 h-7 text-red-500" data-id="element-85" /> What This Isn't
              </h3>
              <ul className="space-y-4 text-gray-700 text-lg" data-id="element-86">
                <li className="flex items-start gap-3" data-id="element-87">
                  <span className="w-2 h-2 rounded-full bg-gray-400 mt-2.5 flex-shrink-0" data-id="element-88"></span>
                  <span data-id="element-89">Not a government agency</span>
                </li>
                <li className="flex items-start gap-3" data-id="element-90">
                  <span className="w-2 h-2 rounded-full bg-gray-400 mt-2.5 flex-shrink-0" data-id="element-91"></span>
                  <span data-id="element-92">Not legal, tax, or financial advice</span>
                </li>
                <li className="flex items-start gap-3" data-id="element-93">
                  <span className="w-2 h-2 rounded-full bg-gray-400 mt-2.5 flex-shrink-0" data-id="element-94"></span>
                  <span data-id="element-95">
                    Not a promise of approval or guaranteed eligibility
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-96">
                  <span className="w-2 h-2 rounded-full bg-gray-400 mt-2.5 flex-shrink-0" data-id="element-97"></span>
                  <span data-id="element-98">
                    Not a substitute for official program requirements
                  </span>
                </li>
              </ul>
            </div>
          </div>
          <p className="text-center text-xl font-serif font-bold text-brand-dark mt-10 italic" data-id="element-99">
            What it does do is help you stop starting from scratch.
          </p>
        </div>
      </section>

      {/* ===== 5. PROBLEM / PAIN SECTION ===== */}
      <section className="bg-brand-dark text-white py-16 md:py-24 px-6" data-id="element-100">
        <div className="max-w-3xl mx-auto text-center" data-id="element-101">
          <AlertTriangle className="w-12 h-12 text-brand-gold mx-auto mb-6" data-id="element-102" />
          <h2 className="font-serif text-3xl md:text-4xl font-bold mb-8 leading-tight" data-id="element-103">
            The real problem isn't always lack of help — it's lack of clarity
          </h2>
          <div className="text-lg md:text-xl text-gray-300 leading-relaxed text-left space-y-6" data-id="element-104">
            <p data-id="element-105">
              Many seniors and families don't miss opportunities on purpose.
            </p>
            <p data-id="element-106">They miss them because:</p>
            <ul className="space-y-4 bg-white/5 p-8 rounded-2xl border border-white/10" data-id="element-107">
              <li className="flex items-start gap-3" data-id="element-108">
                <X className="w-6 h-6 text-brand-gold flex-shrink-0 mt-0.5" data-id="element-109" />
                <span data-id="element-110">the information is spread across too many places</span>
              </li>
              <li className="flex items-start gap-3" data-id="element-111">
                <X className="w-6 h-6 text-brand-gold flex-shrink-0 mt-0.5" data-id="element-112" />
                <span data-id="element-113">the language is hard to follow</span>
              </li>
              <li className="flex items-start gap-3" data-id="element-114">
                <X className="w-6 h-6 text-brand-gold flex-shrink-0 mt-0.5" data-id="element-115" />
                <span data-id="element-116">it's unclear what applies to them</span>
              </li>
              <li className="flex items-start gap-3" data-id="element-117">
                <X className="w-6 h-6 text-brand-gold flex-shrink-0 mt-0.5" data-id="element-118" />
                <span data-id="element-119">they don't know where to start</span>
              </li>
              <li className="flex items-start gap-3" data-id="element-120">
                <X className="w-6 h-6 text-brand-gold flex-shrink-0 mt-0.5" data-id="element-121" />
                <span data-id="element-122">they assume they probably won't qualify anyway</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* ===== 6. SOLUTION INTRO ===== */}
      <section className="bg-brand-cream py-16 md:py-24 px-6" data-id="element-123">
        <div className="max-w-5xl mx-auto" data-id="element-124">
          <div className="flex flex-col lg:flex-row items-center gap-12" data-id="element-125">
            <div className="flex-1" data-id="element-126">
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-brand-dark mb-4" data-id="element-127">
                Introducing: The Senior Benefit Unlocker Guide
              </h2>
              <p className="text-xl text-gray-700 mb-8" data-id="element-128">
                A simple digital resource created to help you review
                senior-related benefits, discounts, and assistance opportunities
                in one organized place.
              </p>
              <ul className="space-y-4 mb-10 text-lg text-gray-700" data-id="element-129">
                <li className="flex items-start gap-3" data-id="element-130">
                  <CheckCircle2 className="w-6 h-6 text-brand-gold flex-shrink-0 mt-0.5" data-id="element-131" />
                  <span data-id="element-132">Benefit categories worth checking</span>
                </li>
                <li className="flex items-start gap-3" data-id="element-133">
                  <CheckCircle2 className="w-6 h-6 text-brand-gold flex-shrink-0 mt-0.5" data-id="element-134" />
                  <span data-id="element-135">Savings opportunities seniors often overlook</span>
                </li>
                <li className="flex items-start gap-3" data-id="element-136">
                  <CheckCircle2 className="w-6 h-6 text-brand-gold flex-shrink-0 mt-0.5" data-id="element-137" />
                  <span data-id="element-138">Simpler eligibility guidance</span>
                </li>
                <li className="flex items-start gap-3" data-id="element-139">
                  <CheckCircle2 className="w-6 h-6 text-brand-gold flex-shrink-0 mt-0.5" data-id="element-140" />
                  <span data-id="element-141">Clearer next-step direction</span>
                </li>
                <li className="flex items-start gap-3" data-id="element-142">
                  <CheckCircle2 className="w-6 h-6 text-brand-gold flex-shrink-0 mt-0.5" data-id="element-143" />
                  <span data-id="element-144">Easy digital access</span>
                </li>
              </ul>
              <CTAButton href="#checkout" data-id="element-145">Get My Guide Now</CTAButton>
            </div>
            <div className="flex-1 w-full max-w-md" data-id="element-146">
              <img src="https://cdn.magicpatterns.com/uploads/9dHnNj5kJDczNG2eEUYH8Y/The_Senior_Benefit_Unlocker_main_image.png" alt="The Senior Benefit Unlocker Guide" className="w-full h-auto rounded-2xl shadow-xl" data-id="element-147" />
            </div>
          </div>
        </div>
      </section>

      {/* ===== 7. WHAT'S INSIDE ===== */}
      <section id="whats-inside" className="bg-white py-16 md:py-24 px-6" data-id="element-148">
        <div className="max-w-5xl mx-auto" data-id="element-149">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-brand-dark mb-12 text-center" data-id="element-150">
            Here's What You'll Get Inside
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12" data-id="element-151">
            <div className="bg-brand-cream p-8 rounded-2xl border border-gray-100 shadow-sm" data-id="element-152">
              <BookOpen className="w-8 h-8 text-brand-gold mb-4" data-id="element-153" />
              <h3 className="font-bold text-xl text-brand-dark mb-3" data-id="element-154">
                The Core Guide
              </h3>
              <p className="text-gray-700 text-lg" data-id="element-155">
                A plain-English guide that organizes benefit categories, savings
                opportunities, and practical next-step information.
              </p>
            </div>
            <div className="bg-brand-cream p-8 rounded-2xl border border-gray-100 shadow-sm" data-id="element-156">
              <Search className="w-8 h-8 text-brand-gold mb-4" data-id="element-157" />
              <h3 className="font-bold text-xl text-brand-dark mb-3" data-id="element-158">
                Benefits & Savings Overview
              </h3>
              <p className="text-gray-700 text-lg" data-id="element-159">
                See the types of support categories and opportunities many
                seniors may want to explore further.
              </p>
            </div>
            <div className="bg-brand-cream p-8 rounded-2xl border border-gray-100 shadow-sm" data-id="element-160">
              <ListChecks className="w-8 h-8 text-brand-gold mb-4" data-id="element-161" />
              <h3 className="font-bold text-xl text-brand-dark mb-3" data-id="element-162">
                Simpler Eligibility Guidance
              </h3>
              <p className="text-gray-700 text-lg" data-id="element-163">
                Understand common qualification factors in easier language.
              </p>
            </div>
            <div className="bg-brand-cream p-8 rounded-2xl border border-gray-100 shadow-sm" data-id="element-164">
              <ArrowRight className="w-8 h-8 text-brand-gold mb-4" data-id="element-165" />
              <h3 className="font-bold text-xl text-brand-dark mb-3" data-id="element-166">
                Action-Oriented Next Steps
              </h3>
              <p className="text-gray-700 text-lg" data-id="element-167">
                Know how to move from confusion to clarity faster.
              </p>
            </div>
          </div>
          <div className="text-center" data-id="element-168">
            <CTAButton href="#checkout" data-id="element-169">
              Get Instant Access —{' '}
              {bumpCount === 0 && <span className="line-through opacity-70 mx-1" data-id="element-170">$32</span>}
              ${totalPrice}.00
            </CTAButton>
          </div>
        </div>
      </section>

      {/* ===== 8. COMPARISON SECTION ===== */}
      <section className="bg-brand-dark text-white py-16 md:py-24 px-6" data-id="element-171">
        <div className="max-w-5xl mx-auto" data-id="element-172">
          <h2 className="font-serif text-3xl md:text-4xl font-bold mb-12 text-center" data-id="element-173">
            Why this is easier than trying to search on your own
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-174">
            <div className="bg-white/5 p-8 rounded-2xl border border-white/10" data-id="element-175">
              <h3 className="font-serif text-2xl font-bold text-gray-300 mb-6 flex items-center gap-3" data-id="element-176">
                <X className="w-6 h-6 text-red-400" data-id="element-177" /> Searching on your own
              </h3>
              <ul className="space-y-4 text-lg text-gray-400" data-id="element-178">
                <li className="flex items-start gap-3" data-id="element-179">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-500 mt-2.5 flex-shrink-0" data-id="element-180"></span>
                  <span data-id="element-181">Dozens of scattered websites</span>
                </li>
                <li className="flex items-start gap-3" data-id="element-182">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-500 mt-2.5 flex-shrink-0" data-id="element-183"></span>
                  <span data-id="element-184">Confusing jargon</span>
                </li>
                <li className="flex items-start gap-3" data-id="element-185">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-500 mt-2.5 flex-shrink-0" data-id="element-186"></span>
                  <span data-id="element-187">Hard to know where to begin</span>
                </li>
                <li className="flex items-start gap-3" data-id="element-188">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-500 mt-2.5 flex-shrink-0" data-id="element-189"></span>
                  <span data-id="element-190">Easy to miss categories</span>
                </li>
                <li className="flex items-start gap-3" data-id="element-191">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-500 mt-2.5 flex-shrink-0" data-id="element-192"></span>
                  <span data-id="element-193">Time-consuming and frustrating</span>
                </li>
              </ul>
            </div>

            <div className="bg-brand-gold/10 p-8 rounded-2xl border border-brand-gold/30" data-id="element-194">
              <h3 className="font-serif text-2xl font-bold text-brand-gold mb-6 flex items-center gap-3" data-id="element-195">
                <CheckCircle2 className="w-6 h-6 text-brand-gold" data-id="element-196" /> Using this
                guide
              </h3>
              <ul className="space-y-4 text-lg text-white" data-id="element-197">
                <li className="flex items-start gap-3" data-id="element-198">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-gold mt-2.5 flex-shrink-0" data-id="element-199"></span>
                  <span data-id="element-200">One organized resource</span>
                </li>
                <li className="flex items-start gap-3" data-id="element-201">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-gold mt-2.5 flex-shrink-0" data-id="element-202"></span>
                  <span data-id="element-203">Plain-English explanations</span>
                </li>
                <li className="flex items-start gap-3" data-id="element-204">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-gold mt-2.5 flex-shrink-0" data-id="element-205"></span>
                  <span data-id="element-206">Clearer starting point</span>
                </li>
                <li className="flex items-start gap-3" data-id="element-207">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-gold mt-2.5 flex-shrink-0" data-id="element-208"></span>
                  <span data-id="element-209">Structured overview</span>
                </li>
                <li className="flex items-start gap-3" data-id="element-210">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-gold mt-2.5 flex-shrink-0" data-id="element-211"></span>
                  <span data-id="element-212">Faster and simpler review</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ===== 9. WHO THIS IS FOR ===== */}
      <section className="bg-brand-cream py-16 md:py-24 px-6" data-id="element-213">
        <div className="max-w-3xl mx-auto" data-id="element-214">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-brand-dark mb-10 text-center" data-id="element-215">
            This Guide Is For You If...
          </h2>
          <div className="bg-white p-8 md:p-10 rounded-2xl shadow-sm border border-gray-200" data-id="element-216">
            <ul className="space-y-6 text-lg text-gray-700" data-id="element-217">
              <li className="flex items-start gap-4" data-id="element-218">
                <CheckCircle2 className="w-6 h-6 text-brand-gold flex-shrink-0 mt-1" data-id="element-219" />
                <span data-id="element-220">
                  You're a senior, retiree, or near-retiree who wants to explore
                  possible benefits or savings opportunities
                </span>
              </li>
              <li className="flex items-start gap-4" data-id="element-221">
                <CheckCircle2 className="w-6 h-6 text-brand-gold flex-shrink-0 mt-1" data-id="element-222" />
                <span data-id="element-223">
                  You help a parent, spouse, or loved one navigate
                  senior-related programs
                </span>
              </li>
              <li className="flex items-start gap-4" data-id="element-224">
                <CheckCircle2 className="w-6 h-6 text-brand-gold flex-shrink-0 mt-1" data-id="element-225" />
                <span data-id="element-226">
                  You're tired of trying to decode confusing websites and
                  eligibility language
                </span>
              </li>
              <li className="flex items-start gap-4" data-id="element-227">
                <CheckCircle2 className="w-6 h-6 text-brand-gold flex-shrink-0 mt-1" data-id="element-228" />
                <span data-id="element-229">You want a simple place to start</span>
              </li>
              <li className="flex items-start gap-4" data-id="element-230">
                <CheckCircle2 className="w-6 h-6 text-brand-gold flex-shrink-0 mt-1" data-id="element-231" />
                <span data-id="element-232">
                  You'd rather review one organized guide than search everything
                  manually
                </span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* ===== 10. BONUS SECTION ===== */}
      <section className="bg-white py-16 md:py-24 px-6" data-id="element-233">
        <div className="max-w-4xl mx-auto" data-id="element-234">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-brand-dark mb-12 text-center" data-id="element-235">
            Order Today and Get This Free Bonus
          </h2>

          <div className="bg-brand-cream rounded-2xl border border-brand-gold/30 overflow-hidden shadow-lg" data-id="element-236">
            <div className="bg-brand-gold text-brand-dark text-center py-2 font-bold text-sm uppercase tracking-widest" data-id="element-237">
              FREE BONUS INCLUDED
            </div>
            <div className="p-8 md:p-10 flex flex-col md:flex-row gap-8 items-center" data-id="element-238">
              <div className="w-full md:w-1/3 flex-shrink-0" data-id="element-239">
                <img src="https://cdn.magicpatterns.com/uploads/tsL9YBSV6zZA7vUAYduG33/Bonus_1_The_Senior_Benefits_Bonus_Image.png" alt="The Missing Notice Fast Track Kit (60+ Edition)" className="w-full h-auto rounded-xl shadow-md" data-id="element-240" />
              </div>
              <div className="flex-1" data-id="element-241">
                <h3 className="font-serif text-2xl font-bold text-brand-dark mb-2" data-id="element-242">
                  FREE Bonus: The Missing Notice Fast Track Kit (60+ Edition)
                </h3>
                <p className="text-brand-gold font-bold mb-4" data-id="element-243">Value: $14.00</p>
                <p className="font-serif text-lg font-bold italic text-brand-dark mb-4" data-id="element-244">
                  Find the "Notice" You Were Never Mailed — in 15 Minutes
                </p>
                <p className="text-gray-700 text-lg mb-6 leading-relaxed" data-id="element-245">
                  This kit helps you quickly identify where you may qualify for
                  programs that reduce your monthly expenses — utilities,
                  Medicare-related savings, and food support. It's designed to
                  be simple, fast, and beginner-friendly.
                </p>
                <ul className="space-y-3 text-gray-700" data-id="element-246">
                  <li className="flex items-start gap-2" data-id="element-247">
                    <CheckCircle2 className="w-5 h-5 text-brand-gold flex-shrink-0 mt-0.5" data-id="element-248" />
                    <div data-id="element-249">
                      <span className="font-bold text-brand-dark" data-id="element-250">
                        Your Top 2 Programs
                      </span>
                      <p className="text-sm text-gray-600 mt-0.5" data-id="element-251">
                        A simple, focused list of the two programs most likely
                        to help you — no overwhelm, just clear priorities.
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-252">
                    <CheckCircle2 className="w-5 h-5 text-brand-gold flex-shrink-0 mt-0.5" data-id="element-253" />
                    <div data-id="element-254">
                      <span className="font-bold text-brand-dark" data-id="element-255">
                        Word-for-Word Scripts
                      </span>
                      <p className="text-sm text-gray-600 mt-0.5" data-id="element-256">
                        Exact call scripts you can read aloud — no guessing what
                        to say or how to ask.
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-257">
                    <CheckCircle2 className="w-5 h-5 text-brand-gold flex-shrink-0 mt-0.5" data-id="element-258" />
                    <div data-id="element-259">
                      <span className="font-bold text-brand-dark" data-id="element-260">
                        A Simple Tracker
                      </span>
                      <p className="text-sm text-gray-600 mt-0.5" data-id="element-261">
                        One place to keep names, dates, and case numbers so
                        nothing slips through the cracks.
                      </p>
                    </div>
                  </li>
                </ul>
                <p className="text-sm text-gray-500 italic mt-6" data-id="element-262">
                  This is not legal advice or a government agency. It's a
                  beginner-friendly action guide to help you take confident
                  steps forward.
                </p>
              </div>
            </div>
          </div>

          <div className="text-center mt-10" data-id="element-263">
            <CTAButton href="#checkout" data-id="element-264">Get The Guide + Free Bonus</CTAButton>
          </div>
        </div>
      </section>

      {/* ===== 11. TESTIMONIALS ===== */}
      <section className="bg-brand-cream py-16 md:py-24 px-6" data-id="element-265">
        <div className="max-w-5xl mx-auto" data-id="element-266">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-brand-dark mb-12 text-center" data-id="element-267">
            What Customers Are Saying
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-268">
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100" data-id="element-269">
              <div className="flex gap-1 text-brand-gold mb-4" data-id="element-270">
                <Star className="w-5 h-5 fill-current" data-id="element-271" />
                <Star className="w-5 h-5 fill-current" data-id="element-272" />
                <Star className="w-5 h-5 fill-current" data-id="element-273" />
                <Star className="w-5 h-5 fill-current" data-id="element-274" />
                <Star className="w-5 h-5 fill-current" data-id="element-275" />
              </div>
              <p className="text-gray-700 text-lg italic leading-relaxed" data-id="element-276">
                "I liked that everything was laid out in a simpler way. It gave
                me a much better starting point than trying to search on my
                own."
              </p>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100" data-id="element-277">
              <div className="flex gap-1 text-brand-gold mb-4" data-id="element-278">
                <Star className="w-5 h-5 fill-current" data-id="element-279" />
                <Star className="w-5 h-5 fill-current" data-id="element-280" />
                <Star className="w-5 h-5 fill-current" data-id="element-281" />
                <Star className="w-5 h-5 fill-current" data-id="element-282" />
                <Star className="w-5 h-5 fill-current" data-id="element-283" />
              </div>
              <p className="text-gray-700 text-lg italic leading-relaxed" data-id="element-284">
                "This helped me understand which areas were worth looking into
                without feeling overwhelmed."
              </p>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100" data-id="element-285">
              <div className="flex gap-1 text-brand-gold mb-4" data-id="element-286">
                <Star className="w-5 h-5 fill-current" data-id="element-287" />
                <Star className="w-5 h-5 fill-current" data-id="element-288" />
                <Star className="w-5 h-5 fill-current" data-id="element-289" />
                <Star className="w-5 h-5 fill-current" data-id="element-290" />
                <Star className="w-5 h-5 fill-current" data-id="element-291" />
              </div>
              <p className="text-gray-700 text-lg italic leading-relaxed" data-id="element-292">
                "I got it for my mom and it made the whole process feel a lot
                more manageable."
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== 12. OFFER / PRICING STACK ===== */}
      <section id="checkout" className="bg-white py-16 md:py-24 px-6" data-id="element-293">
        <div className="max-w-3xl mx-auto" data-id="element-294">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-brand-dark mb-10 text-center" data-id="element-295">
            Here's Everything You Get Today
          </h2>

          <div className="bg-brand-cream p-8 md:p-10 rounded-3xl border-2 border-brand-gold shadow-xl mb-10" data-id="element-296">
            <ul className="space-y-4 mb-8 text-lg font-medium text-brand-dark border-b border-gray-200 pb-8" data-id="element-297">
              <li className="flex items-center justify-between gap-3" data-id="element-298">
                <span className="flex items-center gap-3" data-id="element-299">
                  <CheckCircle2 className="w-6 h-6 text-brand-gold flex-shrink-0" data-id="element-300" />
                  The Senior Benefit Unlocker Guide
                </span>
                <span className="text-gray-500 font-normal" data-id="element-301">$17.00</span>
              </li>
              <li className="flex items-center justify-between gap-3" data-id="element-302">
                <span className="flex items-center gap-3" data-id="element-303">
                  <CheckCircle2 className="w-6 h-6 text-brand-gold flex-shrink-0" data-id="element-304" />
                  FREE Bonus: The Missing Notice Fast Track Kit
                </span>
                <span className="text-green-600 font-bold" data-id="element-305">FREE</span>
              </li>
              {bump1Checked && <li className="flex items-center justify-between gap-3" data-id="element-306">
                  <span className="flex items-center gap-3" data-id="element-307">
                    <CheckCircle2 className="w-6 h-6 text-brand-gold flex-shrink-0" data-id="element-308" />
                    The Hidden Wealth Retirement Audit
                  </span>
                  <span className="text-gray-500 font-normal" data-id="element-309">$9.00</span>
                </li>}
              {bump2Checked && <li className="flex items-center justify-between gap-3" data-id="element-310">
                  <span className="flex items-center gap-3" data-id="element-311">
                    <CheckCircle2 className="w-6 h-6 text-brand-gold flex-shrink-0" data-id="element-312" />
                    The Priority Advocacy Master System
                  </span>
                  <span className="text-gray-500 font-normal" data-id="element-313">$9.00</span>
                </li>}
            </ul>

            <div className="text-center mb-8" data-id="element-314">
              <p className="text-gray-500 text-lg mb-2" data-id="element-315">
                Your price today — one-time payment
              </p>
              <div className="flex items-center justify-center gap-3" data-id="element-316">
                {bumpCount === 0 && <span className="text-gray-400 line-through text-2xl font-medium" data-id="element-317">
                    $32.00
                  </span>}
                <span className="text-brand-gold font-black text-5xl" data-id="element-318">
                  ${totalPrice}.00
                </span>
              </div>
              {bumpCount > 0 && <p className="text-sm text-gray-500 mt-2" data-id="element-319">
                  Guide ${basePrice} + {bumpCount} upgrade
                  {bumpCount > 1 ? 's' : ''} ${bumpCount * 9}
                </p>}
            </div>

            <ul className="space-y-3 text-gray-600 text-center max-w-sm mx-auto mb-8" data-id="element-320">
              <li data-id="element-321">Instant digital access</li>
              <li data-id="element-322">Read on any device</li>
              <li data-id="element-323">No subscription</li>
              <li data-id="element-324">30-day money-back guarantee</li>
            </ul>

            <a href={checkoutUrl} onClick={handleCheckoutClick} className="flex items-center justify-center w-full px-8 py-5 text-lg md:text-xl font-bold text-brand-dark bg-brand-gold hover:bg-brand-gold-hover transition-colors rounded-xl shadow-lg" data-id="element-325">
              Get Instant Access — ${totalPrice}.00
              <ArrowRight className="ml-3 w-6 h-6" data-id="element-326" />
            </a>
            <div className="flex items-center justify-center gap-2 mt-3 text-sm text-gray-500" data-id="element-327">
              <Lock className="w-4 h-4" data-id="element-328" />
              <span data-id="element-329">Secure checkout — takes less than 60 seconds</span>
            </div>
          </div>

          {/* ORDER BUMPS */}
          <OrderBumps bump1Checked={bump1Checked} setBump1Checked={setBump1Checked} bump2Checked={bump2Checked} setBump2Checked={setBump2Checked} data-id="element-330" />

          <div className="mt-10" data-id="element-331">
            {bumpCount > 0 && <div className="max-w-md mx-auto bg-brand-cream rounded-xl border border-gray-200 p-4 mb-4 text-sm text-gray-700" data-id="element-332">
                <div className="flex justify-between mb-1" data-id="element-333">
                  <span data-id="element-334">The Senior Benefit Unlocker Guide + Free Bonus</span>
                  <span className="font-bold" data-id="element-335">$17</span>
                </div>
                {bump1Checked && <div className="flex justify-between mb-1" data-id="element-336">
                    <span data-id="element-337">+ The Hidden Wealth Retirement Audit</span>
                    <span className="font-bold" data-id="element-338">$9</span>
                  </div>}
                {bump2Checked && <div className="flex justify-between mb-1" data-id="element-339">
                    <span data-id="element-340">+ The Priority Advocacy Master System</span>
                    <span className="font-bold" data-id="element-341">$9</span>
                  </div>}
                <div className="flex justify-between border-t border-gray-300 pt-2 mt-2 font-bold text-brand-dark text-base" data-id="element-342">
                  <span data-id="element-343">Total Today</span>
                  <span data-id="element-344">${totalPrice}.00</span>
                </div>
              </div>}
            <div className="text-center" data-id="element-345">
              <CTAButton href={checkoutUrl} onClick={handleCheckoutClick} data-id="element-346">
                Get Instant Access
                {bumpCount > 0 ? ' + Upgrades' : ' + Free Bonus'} — $
                {totalPrice}.00
              </CTAButton>
            </div>
            <div className="flex items-center justify-center gap-4 mt-4 text-sm text-gray-500" data-id="element-347">
              <span className="flex items-center gap-1" data-id="element-348">
                <Lock className="w-4 h-4" data-id="element-349" /> Secure checkout
              </span>
              <span className="flex items-center gap-1" data-id="element-350">
                <ShieldCheck className="w-4 h-4" data-id="element-351" /> One-time payment
              </span>
              <span className="flex items-center gap-1" data-id="element-352">
                <Smartphone className="w-4 h-4" data-id="element-353" /> Instant delivery
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* ===== 13. GUARANTEE ===== */}
      <section className="bg-brand-dark text-white py-16 md:py-24 px-6" data-id="element-354">
        <div className="max-w-3xl mx-auto text-center" data-id="element-355">
          <ShieldCheck className="w-16 h-16 text-brand-gold mx-auto mb-6" data-id="element-356" />
          <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6" data-id="element-357">
            30-Day Peace of Mind Guarantee
          </h2>
          <div className="text-lg md:text-xl text-gray-300 leading-relaxed space-y-6 max-w-2xl mx-auto" data-id="element-358">
            <p data-id="element-359">
              Try <strong data-id="element-360">The Senior Benefit Unlocker Guide</strong> for a full
              30 days.
            </p>
            <p data-id="element-361">
              If you don't find it helpful, or if you don't feel it gives you a
              clearer starting point for reviewing benefits, discounts, and
              assistance opportunities, just email us within 30 days for a full
              refund.
            </p>
            <p data-id="element-362">No complicated hoops. No stress.</p>
            <p className="font-bold text-brand-gold" data-id="element-363">
              And yes — you keep the free bonus too.
            </p>
          </div>
          <div className="mt-10" data-id="element-364">
            <CTAButton href="#checkout" data-id="element-365">Try It Risk-Free</CTAButton>
          </div>
        </div>
      </section>

      {/* ===== 14. FAQ ===== */}
      <section className="bg-brand-cream py-16 md:py-24 px-6" data-id="element-366">
        <div className="max-w-3xl mx-auto" data-id="element-367">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-brand-dark mb-10 text-center" data-id="element-368">
            Frequently Asked Questions
          </h2>
          <div className="space-y-2" data-id="element-369">
            <FAQItem question="Is this a physical book or a digital guide?" answer="This is a digital guide. You'll get instant access after purchase and can read it on your phone, tablet, or computer." data-id="element-370" />
            <FAQItem question="Do I have to be low income to use this?" answer="Not necessarily. Some programs depend on income, while others may depend on age, location, household status, service history, or other factors." data-id="element-371" />
            <FAQItem question="Does this guarantee I'll qualify for benefits?" answer="No. This guide does not guarantee approval or eligibility. It is designed to help you identify programs, savings opportunities, and categories worth exploring further." data-id="element-372" />
            <FAQItem question="Is this only for seniors?" answer="It's ideal for seniors, retirees, near-retirees, and adult children or caregivers helping a loved one." data-id="element-373" />
            <FAQItem question="Can I read this on my phone?" answer="Yes. The guide is designed for digital access and can be viewed on phone, tablet, laptop, or desktop." data-id="element-374" />
            <FAQItem question="Is this a subscription?" answer="No. This is a one-time purchase." data-id="element-375" />
            <FAQItem question="How fast do I get access?" answer="Immediately after checkout." data-id="element-376" />
            <FAQItem question="Is this legal or financial advice?" answer="No. This guide is educational and informational only." data-id="element-377" />
            <FAQItem question="Is the free bonus included right away?" answer="Yes. The bonus is delivered with your order." data-id="element-378" />
          </div>
        </div>
      </section>

      {/* ===== 15. FINAL CLOSE ===== */}
      <section className="bg-white py-16 md:py-24 px-6" data-id="element-379">
        <div className="max-w-3xl mx-auto text-center" data-id="element-380">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-brand-dark mb-8 leading-tight" data-id="element-381">
            Don't leave possible savings, support, and benefit opportunities
            sitting unnoticed
          </h2>
          <div className="text-lg md:text-xl text-gray-700 leading-relaxed space-y-6 mb-10" data-id="element-382">
            <p data-id="element-383">The hardest part for most people isn't taking action.</p>
            <p className="font-bold text-brand-dark" data-id="element-384">
              It's knowing where to begin.
            </p>
            <p data-id="element-385">
              <strong data-id="element-386">The Senior Benefit Unlocker Guide</strong> gives you a
              simpler place to start — without the frustration of piecing
              everything together on your own.
            </p>
            <p data-id="element-387">
              If you want a clearer, easier way to review what may be worth
              checking for yourself or a loved one, this guide was made for
              exactly that.
            </p>
          </div>

          <div className="bg-brand-cream p-6 rounded-xl inline-block mb-10 text-left border border-gray-200" data-id="element-388">
            <ul className="space-y-2 text-gray-700 font-medium" data-id="element-389">
              <li className="flex items-center gap-2" data-id="element-390">
                <CheckCircle2 className="w-5 h-5 text-brand-gold" data-id="element-391" /> Get the
                full digital guide
              </li>
              <li className="flex items-center gap-2" data-id="element-392">
                <CheckCircle2 className="w-5 h-5 text-brand-gold" data-id="element-393" /> The free
                application checklist bonus
              </li>
              <li className="flex items-center gap-2" data-id="element-394">
                <CheckCircle2 className="w-5 h-5 text-brand-gold" data-id="element-395" /> Instant
                access
              </li>
              <li className="flex items-center gap-2" data-id="element-396">
                <CheckCircle2 className="w-5 h-5 text-brand-gold" data-id="element-397" /> 30-day
                money-back guarantee
              </li>
            </ul>
          </div>

          <div data-id="element-398">
            {bumpCount > 0 && <div className="max-w-md mx-auto bg-brand-cream rounded-xl border border-gray-200 p-4 mb-4 text-sm text-gray-700" data-id="element-399">
                <div className="flex justify-between mb-1" data-id="element-400">
                  <span data-id="element-401">The Senior Benefit Unlocker Guide + Free Bonus</span>
                  <span className="font-bold" data-id="element-402">$17</span>
                </div>
                {bump1Checked && <div className="flex justify-between mb-1" data-id="element-403">
                    <span data-id="element-404">+ The Hidden Wealth Retirement Audit</span>
                    <span className="font-bold" data-id="element-405">$9</span>
                  </div>}
                {bump2Checked && <div className="flex justify-between mb-1" data-id="element-406">
                    <span data-id="element-407">+ The Priority Advocacy Master System</span>
                    <span className="font-bold" data-id="element-408">$9</span>
                  </div>}
                <div className="flex justify-between border-t border-gray-300 pt-2 mt-2 font-bold text-brand-dark text-base" data-id="element-409">
                  <span data-id="element-410">Total Today</span>
                  <span data-id="element-411">${totalPrice}.00</span>
                </div>
              </div>}
            <div className="text-center" data-id="element-412">
              <CTAButton href={checkoutUrl} onClick={handleCheckoutClick} data-id="element-413">
                Get Instant Access
                {bumpCount > 0 ? ' + Upgrades' : ' + Free Bonus'} — $
                {totalPrice}.00
              </CTAButton>
            </div>
            <div className="flex items-center justify-center gap-4 mt-4 text-sm text-gray-500" data-id="element-414">
              <span className="flex items-center gap-1" data-id="element-415">
                <Lock className="w-4 h-4" data-id="element-416" /> Secure checkout
              </span>
              <span className="flex items-center gap-1" data-id="element-417">
                <ShieldCheck className="w-4 h-4" data-id="element-418" /> One-time payment
              </span>
              <span className="flex items-center gap-1" data-id="element-419">
                <Smartphone className="w-4 h-4" data-id="element-420" /> Instant delivery
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* ===== 16. FOOTER ===== */}
      <footer className="bg-brand-dark text-gray-400 py-12 text-center text-sm px-6" data-id="element-421">
        <div className="max-w-4xl mx-auto" data-id="element-422">
          <div className="flex flex-wrap items-center justify-center gap-6 mb-8" data-id="element-423">
            <button onClick={() => setPrivacyOpen(true)} className="hover:text-white transition-colors" data-id="element-424">
              Privacy Policy
            </button>
            <button onClick={() => setTermsOpen(true)} className="hover:text-white transition-colors" data-id="element-425">
              Terms of Service
            </button>
            <a href="mailto:support@dilorepublishing.com" className="hover:text-white transition-colors" data-id="element-426">
              Contact
            </a>
            <button onClick={() => setTermsOpen(true)} className="hover:text-white transition-colors" data-id="element-427">
              Disclaimer
            </button>
          </div>
          <p className="mb-6 opacity-60 leading-relaxed" data-id="element-428">
            This guide is for educational and informational purposes only and
            does not constitute legal, tax, financial, or benefits eligibility
            advice. Program availability and qualification requirements vary
            based on individual circumstances, location, and official rules.
          </p>
          <p data-id="element-429">
            © {new Date().getFullYear()} Dilore Publishing. All rights
            reserved.
          </p>
        </div>
      </footer>

      {/* ===== STICKY MOBILE CTA ===== */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)] z-40 md:hidden" data-id="element-430">
        {bumpCount > 0 && <div className="px-4 pt-3 pb-1 text-xs text-gray-600 space-y-0.5" data-id="element-431">
            <div className="flex justify-between" data-id="element-432">
              <span data-id="element-433">The Senior Benefit Unlocker Guide + Free Bonus</span>
              <span className="font-bold" data-id="element-434">$17</span>
            </div>
            {bump1Checked && <div className="flex justify-between" data-id="element-435">
                <span data-id="element-436">+ The Hidden Wealth Retirement Audit</span>
                <span className="font-bold" data-id="element-437">$9</span>
              </div>}
            {bump2Checked && <div className="flex justify-between" data-id="element-438">
                <span data-id="element-439">+ The Priority Advocacy Master System</span>
                <span className="font-bold" data-id="element-440">$9</span>
              </div>}
            <div className="flex justify-between border-t border-gray-200 pt-1 mt-1 font-bold text-brand-dark text-sm" data-id="element-441">
              <span data-id="element-442">Total</span>
              <span data-id="element-443">${totalPrice}.00</span>
            </div>
          </div>}
        <div className="p-3" data-id="element-444">
          <a href={checkoutUrl} onClick={handleCheckoutClick} className="flex items-center justify-center w-full py-3.5 px-4 text-base font-bold text-brand-dark bg-brand-gold hover:bg-brand-gold-hover transition-colors rounded-lg shadow-sm" data-id="element-445">
            Get Instant Access — ${totalPrice}.00
          </a>
        </div>
      </div>

      {/* ===== PRIVACY POLICY MODAL ===== */}
      <Modal isOpen={privacyOpen} onClose={() => setPrivacyOpen(false)} title="Privacy Policy" data-id="element-446">
        <p data-id="element-447">
          <strong data-id="element-448">Last Updated:</strong> March 2026
        </p>
        <p data-id="element-449">
          Dilore Publishing ("we," "us," or "our") is committed to protecting
          your privacy. This Privacy Policy explains how we handle information
          when you purchase and use The Senior Benefit Unlocker guide.
        </p>
        <h3 className="font-bold text-brand-dark text-lg mt-4" data-id="element-450">
          Information We Collect
        </h3>
        <p data-id="element-451">
          We collect only the minimum information necessary to process your
          purchase, including your email address and payment details. Payment
          processing is handled securely by our third-party payment processor —
          we never store your credit card information.
        </p>
        <h3 className="font-bold text-brand-dark text-lg mt-4" data-id="element-452">
          How We Use Your Information
        </h3>
        <p data-id="element-453">
          Your email address is used solely to deliver your digital purchase and
          provide order-related communications. We do not sell, rent, or share
          your personal information with third parties for marketing purposes.
        </p>
        <h3 className="font-bold text-brand-dark text-lg mt-4" data-id="element-454">
          Data Security
        </h3>
        <p data-id="element-455">
          We use industry-standard security measures to protect your
          information. All transactions are encrypted using SSL technology.
        </p>
        <h3 className="font-bold text-brand-dark text-lg mt-4" data-id="element-456">Cookies</h3>
        <p data-id="element-457">
          Our website may use essential cookies to ensure proper functionality.
          We do not use tracking cookies for advertising purposes.
        </p>
      </Modal>

      {/* ===== TERMS OF SERVICE MODAL ===== */}
      <Modal isOpen={termsOpen} onClose={() => setTermsOpen(false)} title="Terms of Service & Disclaimer" data-id="element-458">
        <p data-id="element-459">
          <strong data-id="element-460">Last Updated:</strong> March 2026
        </p>
        <p data-id="element-461">
          By purchasing and using The Senior Benefit Unlocker ("the Guide"), you
          agree to the following terms and conditions.
        </p>
        <h3 className="font-bold text-brand-dark text-lg mt-4" data-id="element-462">
          Educational Purpose Only
        </h3>
        <p data-id="element-463">
          The Guide is for informational and educational purposes only. It does
          not constitute legal, financial, or tax advice. We recommend
          consulting with a qualified professional regarding your specific
          situation. Eligibility for government programs varies by state,
          county, and individual circumstances.
        </p>
        <h3 className="font-bold text-brand-dark text-lg mt-4" data-id="element-464">
          No Guarantees
        </h3>
        <p data-id="element-465">
          While the Guide provides actionable steps to identify potential
          benefits, we cannot guarantee specific dollar amounts or approval for
          any government program. Results vary based on individual eligibility,
          location, and program funding availability.
        </p>
        <h3 className="font-bold text-brand-dark text-lg mt-4" data-id="element-466">
          Digital Delivery
        </h3>
        <p data-id="element-467">
          The Guide is a digital product delivered immediately upon purchase.
          Due to the nature of digital products, all sales are final. If you
          experience technical issues accessing your purchase, please reach out
          and we will ensure you receive your product.
        </p>
        <h3 className="font-bold text-brand-dark text-lg mt-4" data-id="element-468">
          Limitation of Liability
        </h3>
        <p data-id="element-469">
          Dilore Publishing shall not be held liable for any outcomes resulting
          from the use of information provided in the Guide. Your use of the
          Guide and any actions taken based on its contents are at your own
          discretion and risk.
        </p>
      </Modal>

      {/* ===== INSIDE PREVIEW MODAL ===== */}
      {previewOpen && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 overflow-y-auto" onClick={() => setPreviewOpen(false)} data-id="element-470">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto relative" onClick={e => e.stopPropagation()} data-id="element-471">
            <button onClick={() => setPreviewOpen(false)} className="absolute top-4 right-4 p-2 rounded-full bg-white/90 hover:bg-gray-100 transition-colors z-10 shadow-md" aria-label="Close preview" data-id="element-472">
              <X className="w-6 h-6 text-gray-600" data-id="element-473" />
            </button>
            <div className="p-6 md:p-10" data-id="element-474">
              <h2 className="font-serif text-2xl md:text-3xl font-bold text-brand-dark mb-2 text-center" data-id="element-475">
                Inside The Senior Benefit Unlocker Guide
              </h2>
              <p className="text-gray-500 text-center mb-8" data-id="element-476">
                Preview pages from the guide
              </p>
              <div className="space-y-6" data-id="element-477">
                <img src="https://cdn.magicpatterns.com/uploads/8Hqr4ALz1MkFuAEzHNnnnw/Image_1_Your-Roadmap-Whats-Inside_The_Senior_Benefit.png" alt="Your Roadmap: What's Inside — chapter overview" className="w-full h-auto rounded-xl shadow-md border border-gray-200" data-id="element-478" />
                <img src="https://cdn.magicpatterns.com/uploads/m2tBFpvuo8y4y2M84eDHzh/Image_2_The-Invisible-Check-in-Your-Mailbox_-_The_Senior_Benefit.png" alt="Chapter 1: The Invisible Check in Your Mailbox" className="w-full h-auto rounded-xl shadow-md border border-gray-200" data-id="element-479" />
                <img src="https://cdn.magicpatterns.com/uploads/5zBuiF2wX7gjNxhdUKT5EM/Image_3_The-dollar18-Billion-Gap_-_The_Senior_Benefit.png" alt="The $18 Billion Gap — why benefits go unclaimed" className="w-full h-auto rounded-xl shadow-md border border-gray-200" data-id="element-480" />
              </div>
              <div className="text-center mt-8" data-id="element-481">
                <a href="#checkout" onClick={() => setPreviewOpen(false)} className="inline-flex items-center justify-center px-8 py-5 text-lg font-bold text-brand-dark bg-brand-gold hover:bg-brand-gold-hover transition-colors rounded-xl shadow-lg" data-id="element-482">
                  Get Instant Access Now
                </a>
              </div>
            </div>
          </div>
        </div>}

      <ExitIntentPopup isOpen={showExitPopup} onClose={() => setShowExitPopup(false)} data-id="element-483" />

      <RecentPurchaseToast data-id="element-484" />
    </div>;
}